import BasicAPI, {Warning} from "./basic";

export class SignUpUserResult extends BasicAPI {
    constructor(params) {
        super();
        /**
         * @type {String}
         */
        this.success = params.success;

        if (params.warnings) {
            /**
             * @type {Array<Warning>}
             */
            this.warnings = params.warnings.map(warning => new Warning(warning));
        }
    }

    /**
     * Валидация полей
     * @param expect {Expect<{}>}
     * @param test {TestType<PlaywrightTestArgs & PlaywrightTestOptions, PlaywrightWorkerArgs & PlaywrightWorkerOptions>}
     */
    validate({expect, test}) {
        if (!this.success) {
            expect(this.warnings).toBeTruthy();

            for (let warning of this.warnings) {
                test.step(`Проверка предупреждения ${warning.warningCode}`, () => {
                    warning.validate({expect})
                })
            }
        }
    }
}

export class SignUpUserParams {
    /**
     * @param username {String}
     * @param email {String}
     * @param password {String}
     * @param firstname {String}
     * @param lastname {String}
     * @param middlename {String|null}
     * @param city {String}
     * @param country {String}
     * @param recaptchaChallengeHash {String}
     * @param recaptchaResponse {String}
     * @param customProfileFields
     * @param redirect {String|null}
     */
    constructor({username, email, password, firstname, lastname, middlename, city, country, recaptchaChallengeHash, recaptchaResponse, customProfileFields, redirect}) {
        this.username = username;
        this.email = email;
        this.password = password;
        this.firstname = firstname;
        this.lastname = lastname;
        this.middlename = middlename;
        this.city = city;
        this.country = country;
        this.recaptchachallengehash = recaptchaChallengeHash;
        this.recaptcharesponse = recaptchaResponse;
        this.customprofilefields = customProfileFields;
        this.redirect = redirect || `${window.location.origin}/auth/confirm/`;

    }

    get recaptchaChallengeHash() {
        return this.recaptchachallengehash
    }

    get customProfileFields() {
        return this.customprofilefields;
    }

    get recaptchaResponse() {
        return this.recaptcharesponse;
    }
}

export class CustomProfileFields {
    constructor(params) {
        /**
         * @type {String}
         */
        this.type = params.type;
        /**
         * @type {String}
         */
        this.name = params.name;
        /**
         * @type {String}
         */
        this.value = params.value;
    }
}

export class SignUpSettings extends BasicAPI {
    /**
     * Здесь перечислена только часть полей API. За полным набором полей обращайтесь к документации LMS
     * @param settings {Object<string, any>}
     */
    constructor(settings) {
        super();
        /**
         * @type {String}
         */
        this.passwordpolicy = settings.passwordpolicy;
        /**
         * @type {String}
         */
        this.sitepolicy = settings.sitepolicy;
        /**
         * @type {String}
         */
        this.sitepolicyhandler = settings.sitepolicyhandler;
        /**
         * @type {String}
         */
        this.defaultcity = settings.defaultcity;
        /**
         * @type {String}
         */
        this.country = settings.country;
        /**
         * @type {String[]}
         */
        this.namefields = settings.namefields;
        /**
         * @type {Warning[]}
         */
        this.warnings = (settings.warnings || []).map(warning => new Warning(warning));
    }
    /**
     * Валидация полей
     * @param expect {Expect<{}>}
     * @param test {TestType<PlaywrightTestArgs & PlaywrightTestOptions, PlaywrightWorkerArgs & PlaywrightWorkerOptions>}
     */
    validate({expect, test}) {
        expect(this.country).toBeTruthy();
        expect(this.defaultcity).toBeTruthy();

        test.step('Проверка namefields', () => {
            for (const nameField of this.namefields) {
                expect(nameField).toBeTruthy();
            }
        })

        if (this.warnings.length > 0) {
            for (let warning of this.warnings) {
                test.step(`Проверка предупреждения ${warning.warningCode}`, () => {
                    warning.validate({expect})
                })
            }
        }
    }
}

export class SingInParams {
    constructor() {
        this._email = '';
        this.password = '';
    }
    get email() {
        return this._email;
    }
    set email(email) {
        this._email = email.toLowerCase();
    }
}

export class SignInResult extends BasicAPI {
    constructor(params) {
        super();
        /**
         * @type {String}
         */
        this.token = params.token;
        /**
         * @type {String}
         */
        this.privatetoken = params.privatetoken;
    }

    /**
     * Валидация полей
     * @param expect {Expect<{}>}
     * @param test {TestType<PlaywrightTestArgs & PlaywrightTestOptions, PlaywrightWorkerArgs & PlaywrightWorkerOptions>}
     */
    validate({expect}) {
        expect(this.token).toBeTruthy();
        expect(this.privatetoken).toBeTruthy();
    }
}

export class GetUserDataParams {
    constructor(params) {
        /**
         * @type {String}
         */
        this.email = params.email;
    }
}

export class GetUserDataResult extends BasicAPI {
    constructor(data) {
        super();

        this.id = data.id;
        this.username = data.username;
        this.firstname = data.firstname;
        this.lastname = data.lastname;
        this.fullname = data.fullname || `${data.firstname} ${data.lastname}`;
        this.email = data.email;
        this.firstaccess = data.firstaccess;
        this.lastaccess = data.lastaccess;
        this.auth = data.auth;
        this.suspended = data.suspended;
        this.confirmed = data.confirmed;
        this.lang = data.lang;
        this.description = data.description;
        this.profileimageurlsmall = data.profileimageurlsmall;
        this.profileimageurl = data.profileimageurl;
        this.preferences = data.preferences || {
            tool_yandexcal_time_expires: null,
            auth_forcepasswordchange: false,
            ldap_password_change_url: null
        };
    }

    async validate({expect, test, request}) {
        const trusted = [
            this.id,
            this.username,
            this.firstname,
            this.lastname,
            this.fullname,
            this.email,
            this.auth,
            this.confirmed,
            this.lang,
            this.timezone,
            this.profileimageurlsmall,
            this.profileimageurl
        ]

        for (let tr of trusted) {
            expect(this[tr]).toBeTruthy();
        }

        await expect((await request.get(this.profileimageurlsmall)).ok()).toBeTruthy();
        await expect((await request.get(this.profileimageurl)).ok()).toBeTruthy();

    }
}
